import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Plus, Shield, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { cn, formatDateTime } from '@/lib/utils';

interface Tom {
  area: string;
  measure: string;
}
interface Subprocessor {
  name: string;
  address: string;
  service: string;
  location: string;
  safeguards: string;
}
interface ServiceProfile {
  key: string;
  label: string;
  description: string;
  services: string[];
  dataCategories: string[];
  dataSubjects: string[];
  purposes: string[];
}
interface Profile {
  dpoName: string | null;
  dpoEmail: string | null;
  dpoPhone: string | null;
  dpoNotRequired: boolean;
  supervisoryAuthority: string | null;
  serviceProfiles: string[];
  services: string[];
  dataCategories: string[];
  dataSubjects: string[];
  purposes: string[];
  toms: Tom[];
  subprocessors: Subprocessor[];
  breachNotificationHours: number;
  deletionDays: number;
}
interface ProfileView extends Profile {
  completedAt: string | null;
  templateId: string | null;
  exists: boolean;
  catalog: { serviceProfiles: ServiceProfile[]; tomAreas: string[]; toms: (Tom & { default: boolean })[]; subprocessors: Subprocessor[] };
}

const onError = (e: unknown) => toast.error(errorMessage(e));
const STEPS = ['Datenschutzkontakt', 'Leistungen', 'Daten und Betroffene', 'Maßnahmen (TOMs)', 'Unterauftragsverarbeiter', 'Fristen und Abschluss'];

/** Mehrzeiliges Listenfeld (ein Eintrag pro Zeile). */
function LinesField({ label, value, onChange, hint }: { label: string; value: string[]; onChange: (v: string[]) => void; hint?: string }) {
  const [text, setText] = useState(value.join('\n'));
  useEffect(() => setText(value.join('\n')), [value]);
  return (
    <Field label={label} hint={hint ?? 'Ein Eintrag pro Zeile'}>
      <Textarea rows={Math.max(3, value.length + 1)} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => onChange(text.split('\n').map((l) => l.trim()).filter(Boolean))} />
    </Field>
  );
}

const uniq = (a: string[]) => [...new Set(a)];

function Wizard({ data }: { data: ProfileView }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [p, setP] = useState<Profile>(data);
  const [customTom, setCustomTom] = useState<Tom>({ area: data.catalog.tomAreas[0] ?? '', measure: '' });
  useEffect(() => setP(data), [data]);
  const set = <K extends keyof Profile>(k: K, v: Profile[K]) => setP((x) => ({ ...x, [k]: v }));

  const body = () => ({ ...p, dpoEmail: p.dpoEmail || null });
  const save = useMutation({
    mutationFn: () => api<ProfileView>('/tenant/data-protection', { method: 'PUT', body: body() }),
    onSuccess: (v) => qc.setQueryData(['tenant', 'data-protection'], v),
    onError,
  });
  const complete = useMutation({
    mutationFn: () => api<ProfileView>('/tenant/data-protection/complete', { method: 'POST', body: body() }),
    onSuccess: (v) => {
      qc.setQueryData(['tenant', 'data-protection'], v);
      qc.invalidateQueries({ queryKey: ['contract-templates'] });
      toast.success('AVV-Vorlage erstellt – AVVs können jetzt je Kunde angelegt werden');
    },
    onError,
  });
  const go = (to: number) => {
    save.mutate();
    setStep(to);
  };

  const toggleProfile = (sp: ServiceProfile) => {
    const on = p.serviceProfiles.includes(sp.key);
    setP((x) => {
      const profiles = on ? x.serviceProfiles.filter((k) => k !== sp.key) : [...x.serviceProfiles, sp.key];
      // Inhalte der gewählten Vorlagen zusammenführen; eigene Einträge bleiben erhalten
      const chosen = data.catalog.serviceProfiles.filter((s) => profiles.includes(s.key));
      const merge = (field: 'services' | 'dataCategories' | 'dataSubjects' | 'purposes') => {
        const fromRemoved = on ? sp[field] : [];
        const kept = x[field].filter((v) => !fromRemoved.includes(v) || chosen.some((c) => c[field].includes(v)));
        return uniq([...kept, ...chosen.flatMap((c) => c[field])]);
      };
      return { ...x, serviceProfiles: profiles, services: merge('services'), dataCategories: merge('dataCategories'), dataSubjects: merge('dataSubjects'), purposes: merge('purposes') };
    });
  };
  const hasTom = (t: Tom) => p.toms.some((x) => x.area === t.area && x.measure === t.measure);
  const toggleTom = (t: Tom) => set('toms', hasTom(t) ? p.toms.filter((x) => !(x.area === t.area && x.measure === t.measure)) : [...p.toms, { area: t.area, measure: t.measure }]);
  const customToms = p.toms.filter((t) => !data.catalog.toms.some((c) => c.area === t.area && c.measure === t.measure));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="size-4 text-primary" /> Assistent: Auftragsverarbeitung (Art. 28 DSGVO)
        </CardTitle>
        <CardDescription>Einmal ausfüllen – daraus entsteht die AVV-Vorlage, mit der Sie für jeden Kunden einen Vertrag zur Auftragsverarbeitung erzeugen.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        <ol className="flex flex-wrap gap-1.5" aria-label="Schritte">
          {STEPS.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                onClick={() => go(i)}
                aria-current={step === i ? 'step' : undefined}
                className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs', step === i ? 'border-primary bg-primary/10 font-medium text-primary' : i < step ? 'text-foreground' : 'text-muted-foreground')}
              >
                <span className={cn('inline-flex size-4 items-center justify-center rounded-full text-[10px]', i < step ? 'bg-success text-white' : 'bg-muted')}>{i < step ? <Check className="size-3" /> : i + 1}</span>
                {s}
              </button>
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Switch checked={p.dpoNotRequired} onCheckedChange={(v) => set('dpoNotRequired', v)} /> Kein Datenschutzbeauftragter benannt (Kontakt über die Geschäftsführung)
            </label>
            {!p.dpoNotRequired && (
              <>
                <Field label="Datenschutzbeauftragter (Name bzw. Firma)" htmlFor="dpo-name">
                  <Input id="dpo-name" value={p.dpoName ?? ''} onChange={(e) => set('dpoName', e.target.value)} />
                </Field>
                <Field label="E-Mail" htmlFor="dpo-mail">
                  <Input id="dpo-mail" type="email" value={p.dpoEmail ?? ''} onChange={(e) => set('dpoEmail', e.target.value)} />
                </Field>
                <Field label="Telefon" htmlFor="dpo-phone">
                  <Input id="dpo-phone" value={p.dpoPhone ?? ''} onChange={(e) => set('dpoPhone', e.target.value)} />
                </Field>
              </>
            )}
            <Field label="Zuständige Aufsichtsbehörde" htmlFor="dpo-auth" hint="z. B. Landesbeauftragte für Datenschutz und Informationsfreiheit NRW">
              <Input id="dpo-auth" value={p.supervisoryAuthority ?? ''} onChange={(e) => set('supervisoryAuthority', e.target.value)} />
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-4">
            <p className="text-sm text-muted-foreground">Welche Leistungen erbringen Sie, bei denen Sie auf personenbezogene Daten Ihrer Kunden zugreifen? Die Vorlagen füllen die nächsten Schritte vor.</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {data.catalog.serviceProfiles.map((sp) => (
                <label key={sp.key} className={cn('flex cursor-pointer gap-3 rounded-lg border p-3 text-sm', p.serviceProfiles.includes(sp.key) && 'border-primary bg-primary/5')}>
                  <Checkbox checked={p.serviceProfiles.includes(sp.key)} onCheckedChange={() => toggleProfile(sp)} className="mt-0.5" />
                  <span>
                    <span className="block font-medium">{sp.label}</span>
                    <span className="text-muted-foreground">{sp.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <LinesField label="Leistungen (Gegenstand der Verarbeitung)" value={p.services} onChange={(v) => set('services', v)} />
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-4 lg:grid-cols-3">
            <LinesField label="Art der personenbezogenen Daten" value={p.dataCategories} onChange={(v) => set('dataCategories', v)} />
            <LinesField label="Kategorien betroffener Personen" value={p.dataSubjects} onChange={(v) => set('dataSubjects', v)} />
            <LinesField label="Zwecke der Verarbeitung" value={p.purposes} onChange={(v) => set('purposes', v)} />
          </div>
        )}

        {step === 3 && (
          <div className="grid gap-4">
            <p className="text-sm text-muted-foreground">Häufige Maßnahmen sind vorausgewählt. Bitte nur ankreuzen, was in Ihrem Betrieb tatsächlich umgesetzt ist, und Eigenes ergänzen.</p>
            {data.catalog.tomAreas.map((area) => (
              <fieldset key={area} className="grid gap-1.5">
                <legend className="mb-1 text-sm font-medium">{area}</legend>
                {data.catalog.toms
                  .filter((t) => t.area === area)
                  .map((t) => (
                    <label key={t.measure} className="flex items-start gap-2 text-sm">
                      <Checkbox checked={hasTom(t)} onCheckedChange={() => toggleTom(t)} className="mt-0.5" /> {t.measure}
                    </label>
                  ))}
                {customToms
                  .filter((t) => t.area === area)
                  .map((t) => (
                    <label key={t.measure} className="flex items-start gap-2 text-sm">
                      <Checkbox checked onCheckedChange={() => toggleTom(t)} className="mt-0.5" /> {t.measure} <Badge variant="outline">eigene</Badge>
                    </label>
                  ))}
              </fieldset>
            ))}
            <div className="grid gap-2 rounded-md border border-dashed p-3 sm:grid-cols-[16rem_1fr_auto]">
              <select className="h-9 rounded-md border bg-card px-2 text-sm" value={customTom.area} onChange={(e) => setCustomTom((c) => ({ ...c, area: e.target.value }))} aria-label="Bereich">
                {data.catalog.tomAreas.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
              <Input value={customTom.measure} onChange={(e) => setCustomTom((c) => ({ ...c, measure: e.target.value }))} placeholder="Eigene Maßnahme" aria-label="Eigene Maßnahme" />
              <Button
                type="button"
                variant="outline"
                disabled={!customTom.measure.trim()}
                onClick={() => {
                  set('toms', [...p.toms, { area: customTom.area, measure: customTom.measure.trim() }]);
                  setCustomTom((c) => ({ ...c, measure: '' }));
                }}
              >
                <Plus /> Hinzufügen
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">Dienstleister, die bei Ihren Leistungen Kundendaten verarbeiten (z. B. Microsoft 365, Hosting). Angaben aus den Vorschlägen bitte mit Ihren Verträgen abgleichen.</p>
            {p.subprocessors.map((s, i) => (
              <div key={i} className="grid gap-2 rounded-md border p-3 sm:grid-cols-2">
                <Input value={s.name} onChange={(e) => set('subprocessors', p.subprocessors.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Unternehmen" aria-label="Unternehmen" />
                <Input value={s.address} onChange={(e) => set('subprocessors', p.subprocessors.map((x, j) => (j === i ? { ...x, address: e.target.value } : x)))} placeholder="Anschrift" aria-label="Anschrift" />
                <Input value={s.service} onChange={(e) => set('subprocessors', p.subprocessors.map((x, j) => (j === i ? { ...x, service: e.target.value } : x)))} placeholder="Leistung" aria-label="Leistung" />
                <Input value={s.location} onChange={(e) => set('subprocessors', p.subprocessors.map((x, j) => (j === i ? { ...x, location: e.target.value } : x)))} placeholder="Ort der Verarbeitung" aria-label="Ort" />
                <Input className="sm:col-span-2" value={s.safeguards} onChange={(e) => set('subprocessors', p.subprocessors.map((x, j) => (j === i ? { ...x, safeguards: e.target.value } : x)))} placeholder="Garantien (z. B. AVV, Standardvertragsklauseln)" aria-label="Garantien" />
                <div className="sm:col-span-2">
                  <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => set('subprocessors', p.subprocessors.filter((_, j) => j !== i))}>
                    <Trash2 /> Entfernen
                  </Button>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {data.catalog.subprocessors
                .filter((s) => !p.subprocessors.some((x) => x.name === s.name))
                .map((s) => (
                  <Button key={s.name} type="button" size="sm" variant="outline" onClick={() => set('subprocessors', [...p.subprocessors, s])}>
                    <Plus /> {s.name}
                  </Button>
                ))}
              <Button type="button" size="sm" variant="outline" onClick={() => set('subprocessors', [...p.subprocessors, { name: '', address: '', service: '', location: '', safeguards: '' }])}>
                <Plus /> Eigener Eintrag
              </Button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Meldung von Datenschutzverletzungen an den Kunden innerhalb von (Stunden)" htmlFor="breach">
                <Input id="breach" type="number" min={1} max={72} value={p.breachNotificationHours} onChange={(e) => set('breachNotificationHours', Number(e.target.value))} />
              </Field>
              <Field label="Löschung bzw. Rückgabe nach Vertragsende innerhalb von (Tagen)" htmlFor="del">
                <Input id="del" type="number" min={0} max={365} value={p.deletionDays} onChange={(e) => set('deletionDays', Number(e.target.value))} />
              </Field>
            </div>
            <div className="rounded-md border p-3 text-sm">
              <p className="font-medium">Zusammenfassung</p>
              <p className="text-muted-foreground">
                {p.services.length} Leistungen · {p.dataCategories.length} Datenarten · {p.dataSubjects.length} Betroffenengruppen · {p.toms.length} Maßnahmen · {p.subprocessors.length} Unterauftragsverarbeiter
              </p>
            </div>
            <Alert>Die AVV-Vorlage enthält Mustertexte ohne Gewähr. Bitte vor dem ersten Einsatz rechtlich prüfen lassen; Anpassungen sind unter Verträge → Vorlagen möglich.</Alert>
            <div>
              <Button onClick={() => complete.mutate()} loading={complete.isPending}>
                <CheckCircle2 /> {data.completedAt ? 'Änderungen übernehmen' : 'Abschließen und AVV-Vorlage erstellen'}
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-between border-t pt-4">
          <Button type="button" variant="outline" disabled={step === 0} onClick={() => go(step - 1)}>
            <ArrowLeft /> Zurück
          </Button>
          {step < STEPS.length - 1 && (
            <Button type="button" onClick={() => go(step + 1)}>
              Weiter <ArrowRight />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface RetentionCounts {
  emailBodies: number;
  auditLogs: number;
  monitoringEvents: number;
  automationRuns: number;
  closedTickets: number;
  ticketAttachments: number;
}
interface RetentionView {
  emailBodyDays: number | null;
  auditLogMonths: number | null;
  monitoringMonths: number | null;
  automationRunMonths: number | null;
  closedTicketMonths: number | null;
  lastRunAt: string | null;
  lastResult: RetentionCounts | null;
  preview: RetentionCounts;
}

const RETENTION_FIELDS: { key: keyof Omit<RetentionView, 'lastRunAt' | 'lastResult' | 'preview'>; label: string; unit: string; hint: string; previewKey: keyof RetentionCounts }[] = [
  { key: 'emailBodyDays', label: 'Texte im E-Mail-Verlauf', unit: 'Tage', hint: 'Absender, Betreff und Ticketbezug bleiben erhalten', previewKey: 'emailBodies' },
  { key: 'closedTicketMonths', label: 'Geschlossene Tickets: Anhänge und Absenderangaben', unit: 'Monate', hint: 'Anhänge werden gelöscht, Absender ohne Kundenkonto entfernt', previewKey: 'closedTickets' },
  { key: 'monitoringMonths', label: 'Behobene Monitoring-Ereignisse', unit: 'Monate', hint: '', previewKey: 'monitoringEvents' },
  { key: 'automationRunMonths', label: 'Automatisierungsprotokoll', unit: 'Monate', hint: '', previewKey: 'automationRuns' },
  { key: 'auditLogMonths', label: 'Audit-Log', unit: 'Monate', hint: 'Mindestens 6 Monate', previewKey: 'auditLogs' },
];

function Retention() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['tenant', 'retention'], queryFn: () => api<RetentionView>('/tenant/retention') });
  const [form, setForm] = useState<Record<string, string>>({});
  useEffect(() => {
    if (data) setForm(Object.fromEntries(RETENTION_FIELDS.map((f) => [f.key, data[f.key]?.toString() ?? ''])));
  }, [data]);
  const save = useMutation({
    mutationFn: () => api<RetentionView>('/tenant/retention', { method: 'PUT', body: Object.fromEntries(RETENTION_FIELDS.map((f) => [f.key, form[f.key] ? Number(form[f.key]) : null])) }),
    onSuccess: (v) => {
      qc.setQueryData(['tenant', 'retention'], v);
      toast.success('Gespeichert');
    },
    onError,
  });
  const run = useMutation({
    mutationFn: () => api<RetentionView>('/tenant/retention/run', { method: 'POST' }),
    onSuccess: (v) => {
      qc.setQueryData(['tenant', 'retention'], v);
      toast.success('Fristen angewendet');
    },
    onError,
  });
  if (!data) return <Skeleton className="h-64" />;
  const pending = Object.values(data.preview).reduce((a, b) => a + b, 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Aufbewahrungsfristen</CardTitle>
        <CardDescription>Werden täglich automatisch angewendet. Leer = nicht automatisch löschen. Kundendaten exportieren oder anonymisieren Sie in der Kundenakte.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {RETENTION_FIELDS.map((f) => (
          <div key={f.key} className="grid items-center gap-2 sm:grid-cols-[1fr_8rem_6rem_10rem]">
            <div>
              <p className="text-sm font-medium">{f.label}</p>
              {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
            </div>
            <Input type="number" min={1} value={form[f.key] ?? ''} onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))} aria-label={f.label} placeholder="nie" />
            <span className="text-sm text-muted-foreground">{f.unit}</span>
            <span className="text-xs text-muted-foreground">{data.preview[f.previewKey] ? `${data.preview[f.previewKey]} betroffen` : 'nichts fällig'}</span>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 border-t pt-4">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Speichern
          </Button>
          <Button variant="outline" onClick={() => run.mutate()} loading={run.isPending} disabled={pending === 0}>
            Jetzt anwenden
          </Button>
          <span className="text-xs text-muted-foreground">{data.lastRunAt ? `Zuletzt angewendet: ${formatDateTime(data.lastRunAt)}` : 'Noch nicht angewendet'}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function PrivacyPage() {
  const [tab, setTab] = useState<'dpa' | 'retention'>('dpa');
  const { data } = useQuery({ queryKey: ['tenant', 'data-protection'], queryFn: () => api<ProfileView>('/tenant/data-protection') });
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Datenschutz" description="Auftragsverarbeitung (AVV) für Ihre Kunden und Aufbewahrungsfristen." />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'dpa', label: 'Auftragsverarbeitung' },
          { value: 'retention', label: 'Aufbewahrungsfristen' },
        ]}
      />
      <div className="mt-4 grid gap-4">
        {tab === 'retention' ? (
          <Retention />
        ) : !data ? (
          <Skeleton className="h-96" />
        ) : (
          <>
            {data.completedAt && (
              <Alert variant="success">
                AVV-Vorlage eingerichtet ({formatDateTime(data.completedAt)}). AVVs legen Sie unter{' '}
                <Link to="/contracts?new=dpa" className="font-medium text-primary hover:underline">
                  Verträge → Neuer AVV
                </Link>{' '}
                an; die Angaben werden beim Anlegen eingefroren.
              </Alert>
            )}
            <Wizard data={data} />
          </>
        )}
      </div>
    </div>
  );
}
